/**
 * Comprime uma imagem no browser antes de fazer upload.
 *
 * Por quê: fotos modernas de celular têm 3-5MB. Subir o original consome banda,
 * tempo (em 4G fraco) e custo de S3. Reduzindo pra max 1920×1080 + JPEG q80
 * ficamos em ~150-400kB sem perda visual perceptível em telas comuns.
 *
 * - Mantém aspect ratio (não estica)
 * - Pula imagens que já estão pequenas (< 600kB) — não vale o custo de CPU
 * - Respeita orientação EXIF (browsers modernos fazem isso automaticamente)
 * - Falha SEGURA: se algo der errado, retorna o original
 */
const MAX_DIM = 1920;
const JPEG_QUALITY = 0.8;
const SKIP_IF_SMALLER_THAN = 600 * 1024;  // 600 kB

export async function compressImageForUpload(file) {
  if (!file || !file.type || !file.type.startsWith("image/")) return file;
  // GIFs animados perdem animação na conversão; preserva original.
  if (file.type === "image/gif") return file;
  if (file.size < SKIP_IF_SMALLER_THAN) return file;

  try {
    const dataUrl = await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
    const img = await new Promise((resolve, reject) => {
      const i = new Image();
      i.onload = () => resolve(i);
      i.onerror = reject;
      i.src = dataUrl;
    });

    let { width, height } = img;
    if (width <= MAX_DIM && height <= MAX_DIM) {
      // Já cabe — pula resize, ainda comprime via canvas se for muito grande
      // pra remover metadados/iCCP perdidos. Mas se file < 1.5MB nem isso vale.
      if (file.size < 1.5 * 1024 * 1024) return file;
    } else {
      const ratio = Math.min(MAX_DIM / width, MAX_DIM / height);
      width = Math.round(width * ratio);
      height = Math.round(height * ratio);
    }

    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(img, 0, 0, width, height);

    const blob = await new Promise((resolve) => {
      canvas.toBlob(resolve, "image/jpeg", JPEG_QUALITY);
    });

    if (!blob) return file;
    // Não substitui se o "comprimido" ficou maior que o original (raro mas possível)
    if (blob.size >= file.size) return file;

    // Renomeia extensão pra .jpg
    const newName = file.name.replace(/\.[^.]+$/, ".jpg") || "image.jpg";
    return new File([blob], newName, { type: "image/jpeg" });
  } catch (e) {
    console.warn("[imageCompress] falha, usando original:", e?.message || e);
    return file;
  }
}
